import React from 'react';
import { Typography } from '@mui/material';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import WbCloudyIcon from '@mui/icons-material/WbCloudy';
import CloudIcon from '@mui/icons-material/Cloud';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import GrainIcon from '@mui/icons-material/Grain';
import UmbrellaIcon from '@mui/icons-material/Umbrella';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ThunderstormIcon from '@mui/icons-material/Thunderstorm';
import AirIcon from '@mui/icons-material/Air';
import dayjs from 'dayjs';
import TSInfoAlert from 'components/common/InfoAlert/InfoAlert';
import { useGetTrailWeatherQuery } from 'app/api/trailApiSlice';

const WEATHER_CODES = {
  0: { label: 'Clear sky', Icon: WbSunnyIcon, color: '#F2B518' },
  1: { label: 'Mainly clear', Icon: WbSunnyIcon, color: '#F2B518' },
  2: { label: 'Partly cloudy', Icon: WbCloudyIcon, color: '#8D8D8D' },
  3: { label: 'Overcast', Icon: CloudIcon, color: '#8D8D8D' },
  45: { label: 'Fog', Icon: CloudQueueIcon, color: '#8D8D8D' },
  48: { label: 'Depositing rime fog', Icon: CloudQueueIcon, color: '#8D8D8D' },
  51: { label: 'Light drizzle', Icon: GrainIcon, color: '#00558C' },
  53: { label: 'Moderate drizzle', Icon: GrainIcon, color: '#00558C' },
  55: { label: 'Dense drizzle', Icon: GrainIcon, color: '#00558C' },
  56: { label: 'Light freezing drizzle', Icon: GrainIcon, color: '#00558C' },
  57: { label: 'Dense freezing drizzle', Icon: GrainIcon, color: '#00558C' },
  61: { label: 'Slight rain', Icon: UmbrellaIcon, color: '#00558C' },
  63: { label: 'Moderate rain', Icon: UmbrellaIcon, color: '#00558C' },
  65: { label: 'Heavy rain', Icon: UmbrellaIcon, color: '#00558C' },
  66: { label: 'Light freezing rain', Icon: UmbrellaIcon, color: '#00558C' },
  67: { label: 'Heavy freezing rain', Icon: UmbrellaIcon, color: '#00558C' },
  71: { label: 'Slight snow', Icon: AcUnitIcon, color: '#5D8AFF' },
  73: { label: 'Moderate snow', Icon: AcUnitIcon, color: '#5D8AFF' },
  75: { label: 'Heavy snow', Icon: AcUnitIcon, color: '#5D8AFF' },
  77: { label: 'Snow grains', Icon: AcUnitIcon, color: '#5D8AFF' },
  80: { label: 'Slight rain showers', Icon: UmbrellaIcon, color: '#00558C' },
  81: { label: 'Moderate rain showers', Icon: UmbrellaIcon, color: '#00558C' },
  82: { label: 'Violent rain showers', Icon: UmbrellaIcon, color: '#00558C' },
  85: { label: 'Slight snow showers', Icon: AcUnitIcon, color: '#5D8AFF' },
  86: { label: 'Heavy snow showers', Icon: AcUnitIcon, color: '#5D8AFF' },
  95: { label: 'Thunderstorm', Icon: ThunderstormIcon, color: '#C10A0A' },
  96: { label: 'Thunderstorm with slight hail', Icon: ThunderstormIcon, color: '#C10A0A' },
  99: { label: 'Thunderstorm with heavy hail', Icon: ThunderstormIcon, color: '#C10A0A' },
};

const describeWeatherCode = (code) =>
  WEATHER_CODES[code]?.label ?? 'Unknown conditions';

const weatherIconFor = (code) => WEATHER_CODES[code] ?? { Icon: CloudIcon, color: '#8D8D8D' };

// Guidance-only — see ADR-0007. This never gates or disables Start
// Activity, it just informs the walker before they decide.
const WeatherForecast = ({ trailId }) => {
  const { data, isLoading, isError } = useGetTrailWeatherQuery(trailId, {
    skip: !trailId,
  });

  if (isLoading || !data || data.hasCoordinates === false) {
    return null;
  }

  if (isError || data.success === false) {
    return (
      <div className="py-2">
        <TSInfoAlert
          info1="Weather forecast unavailable"
          info2="We couldn't load the forecast for this trail right now — check another source before you head out."
          alertType="info-alert"
          alertIcon="Error"
        />
      </div>
    );
  }

  return (
    <div className="py-2">
      <Typography className="py-2" variant="h4">
        Weather Forecast
      </Typography>
      {data.hasWarning && (
        <div className="pb-2">
          <TSInfoAlert
            info1="Severe weather forecast for this trail"
            info2="Check the forecast below before you head out. This is guidance only — the decision to go is yours."
            alertType="warning-alert"
            alertIcon="Warning"
          />
        </div>
      )}
      <div className="flex gap-3 overflow-x-auto pb-1">
        {data.days.map((day) => {
          const { Icon, color } = weatherIconFor(day.weatherCode);
          return (
            <div
              key={day.date}
              className="flex flex-col items-center gap-1 rounded-2xl bg-white border border-solid border-[var(--Grey-20)] shadow-sm px-3 py-3 min-w-[120px] text-center shrink-0"
            >
              <Typography variant="subtitle2" className="!font-semibold">
                {dayjs(day.date).format('ddd D MMM')}
              </Typography>
              <Icon sx={{ fontSize: 32, color }} />
              <Typography variant="body2">{describeWeatherCode(day.weatherCode)}</Typography>
              <Typography variant="body2">
                {Math.round(day.minTempC)}&deg;C to {Math.round(day.maxTempC)}&deg;C
              </Typography>
              <Typography variant="body2">{day.precipitationProbabilityPercent}% rain</Typography>
              <Typography variant="body2" className="flex items-center gap-1">
                <AirIcon sx={{ fontSize: 16 }} /> {Math.round(day.windSpeedMaxKmh)} km/h
              </Typography>
              <Typography variant="body2">
                Feels {Math.round(day.feelsLikeMinC)}&deg;C to {Math.round(day.feelsLikeMaxC)}&deg;C
              </Typography>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WeatherForecast;
